// Painel de relatórios (admin): resumo, evolução, analistas, técnicos, equipamentos e problemas.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Grid, MenuItem, Paper, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs,
  TextField, Typography, makeStyles, useTheme,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import suporteApi, { baixar, mensagemErro } from "../api";
import { dataBR } from "../comum";
import RelatorioGestao from "./RelatorioGestao";

const useStyles = makeStyles((theme) => ({
  corpo: { overflowY: "auto", padding: theme.spacing(1), ...theme.scrollbarStyles },
  filtros: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  tile: { padding: 16, height: "100%" },
  numero: { fontSize: 30, fontWeight: 600, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" },
  bloco: { padding: 16, height: "100%" },
  barra: { height: 8, borderRadius: 4, background: theme.palette.primary.main, minWidth: 2 },
  barraFundo: { background: theme.palette.action.hover, borderRadius: 4, width: "100%" },
  num: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  tabela: { "& td, & th": { padding: "5px 8px", fontSize: 13 } },
}));

const hoje = () => new Date().toISOString().slice(0, 10);
const diasAtras = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const fmt = (v, casas = 0) =>
  v === null || v === undefined ? "-" : Number(v).toLocaleString("pt-BR", { maximumFractionDigits: casas });
const tempo = (min) => {
  if (min === null || min === undefined) return "-";
  const m = Number(min);
  if (m < 60) return `${fmt(m, 1)} min`;
  return `${Math.floor(m / 60)}h${String(Math.round(m % 60)).padStart(2, "0")}`;
};

// ------------------------------------------------------------------ peças
const Numero = ({ rotulo, valor, detalhe }) => {
  const c = useStyles();
  return (
    <Paper variant="outlined" className={c.tile}>
      <Typography variant="body2" color="textSecondary">{rotulo}</Typography>
      <div className={c.numero}>{valor}</div>
      {detalhe && <Typography variant="caption" color="textSecondary">{detalhe}</Typography>}
    </Paper>
  );
};

// Tabela de ranking com barra proporcional na coluna principal
const Ranking = ({ titulo, subtitulo, linhas, colunas, chaveBarra = "total", limite = 15 }) => {
  const c = useStyles();
  const max = Math.max(1, ...linhas.map((l) => Number(l[chaveBarra]) || 0));
  const lista = linhas.slice(0, limite);
  return (
    <Paper variant="outlined" className={c.bloco}>
      <Typography variant="subtitle1"><b>{titulo}</b></Typography>
      {subtitulo && <Typography variant="caption" color="textSecondary">{subtitulo}</Typography>}
      <Table size="small" className={c.tabela}>
        <TableHead>
          <TableRow>
            {colunas.map(([rot, , alinhar]) => <TableCell key={rot} align={alinhar}>{rot}</TableCell>)}
            <TableCell style={{ width: "28%" }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {lista.length === 0 && (
            <TableRow><TableCell colSpan={colunas.length + 1}><Typography color="textSecondary" variant="body2">Sem dados no período.</Typography></TableCell></TableRow>
          )}
          {lista.map((l, i) => (
            <TableRow key={i} hover>
              {colunas.map(([rot, f, alinhar]) => (
                <TableCell key={rot} align={alinhar} className={alinhar === "right" ? c.num : undefined}>{f(l, i)}</TableCell>
              ))}
              <TableCell>
                <div className={c.barraFundo}>
                  <div className={c.barra} style={{ width: `${(100 * (Number(l[chaveBarra]) || 0)) / max}%` }} />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

const Grafico = ({ titulo, dados, x, formatarX, altura = 220 }) => {
  const c = useStyles();
  const tema = useTheme();
  return (
    <Paper variant="outlined" className={c.bloco}>
      <Typography variant="subtitle1"><b>{titulo}</b></Typography>
      <ResponsiveContainer width="100%" height={altura}>
        <BarChart data={dados} margin={{ top: 10, right: 8, left: -18, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={tema.palette.divider} />
          <XAxis dataKey={x} tickFormatter={formatarX} tick={{ fontSize: 11, fill: tema.palette.text.secondary }}
            axisLine={{ stroke: tema.palette.divider }} tickLine={false} minTickGap={8} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: tema.palette.text.secondary }} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ fill: tema.palette.action.hover }} labelFormatter={formatarX}
            formatter={(v) => [fmt(v), "Chamados"]} />
          <Bar dataKey="total" fill={tema.palette.primary.main} radius={[4, 4, 0, 0]} maxBarSize={28} />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

// ------------------------------------------------------------------ página
const RelatorioFila = ({ filaId }) => {
  const c = useStyles();
  const [f, setF] = useState({ de: diasAtras(89), ate: hoje() });
  const [r, setR] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await suporteApi.get(`/api/relatorios/fila/${filaId}`, { params: f });
      setR(data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setCarregando(false);
  }, [filaId, f]);
  useEffect(() => {
    setR(null);
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filaId]);
  const campo = (nome) => ({ value: f[nome], onChange: (e) => setF({ ...f, [nome]: e.target.value }), size: "small" });
  const s = r?.resumo;
  const pct = (a, b) => (b ? `${fmt((100 * a) / b, 1)}%` : "-");
  const nomeCard = (r?.modelo?.nome || "card").toLowerCase();

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 4px" }}>
        <div className={c.filtros}>
          <TextField label="De" type="date" InputLabelProps={{ shrink: true }} {...campo("de")} />
          <TextField label="Até" type="date" InputLabelProps={{ shrink: true }} {...campo("ate")} />
          <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={carregar} disabled={carregando}>
            {carregando ? "Gerando…" : "Gerar"}
          </Button>
          <Button startIcon={<GetAppIcon />}
            onClick={() => baixar(`/api/relatorios/fila/${filaId}/excel`, f).catch((err) => toast.error(mensagemErro(err)))}>
            Excel
          </Button>
        </div>
      </div>
      {r && (
        <Grid container spacing={2} className={c.corpo}>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              {`Período ${dataBR(r.periodo.de, true)} a ${dataBR(r.periodo.ate, true)} · ${r.modelo.nome}s abertos no período`}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={2}><Numero rotulo={`${r.modelo.nome}s`} valor={fmt(s.total)} /></Grid>
          <Grid item xs={6} sm={4} md={2}><Numero rotulo="Em andamento" valor={fmt(s.abertos)} /></Grid>
          <Grid item xs={6} sm={4} md={2}><Numero rotulo="Concluídos" valor={fmt(s.concluidos)} detalhe={pct(s.concluidos, s.total)} /></Grid>
          {r.resultados.map((x) => (
            <Grid item xs={6} sm={4} md={2} key={x.resultado}>
              <Numero rotulo={x.resultado.toLowerCase().replace(/^./, (l) => l.toUpperCase())} valor={fmt(x.total)}
                detalhe={`${pct(x.total, s.concluidos)} dos concluídos`} />
            </Grid>
          ))}
          <Grid item xs={6} sm={4} md={2}>
            <Numero rotulo="Tempo até concluir" valor={s.dias_medio === null ? "-" : `${fmt(s.dias_medio, 1)} dias`} detalhe="média" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Numero rotulo="Formulários respondidos" valor={`${fmt(s.respondidos)} / ${fmt(s.formularios)}`} detalhe={pct(s.respondidos, s.formularios)} />
          </Grid>

          <Grid item xs={12} md={7}>
            <Ranking titulo="Funil do processo" subtitulo={`Quantos ${nomeCard}s passaram por cada etapa · quantos estão nela agora`}
              linhas={r.funil} limite={50}
              colunas={[["Etapa", (l) => l.etapa], ["Passaram", (l) => fmt(l.total), "right"], ["%", (l) => pct(l.total, s.total), "right"],
                ["Agora", (l) => fmt(l.agora), "right"]]} />
          </Grid>
          <Grid item xs={12} md={5}>
            <Grafico titulo={`${r.modelo.nome}s por dia`} dados={r.por_dia} x="dia" formatarX={(d) => dataBR(d, true).slice(0, 5)} />
          </Grid>

          {r.por_campo.map((pc) => (
            <Grid item xs={12} md={6} key={pc.campo}>
              <Ranking titulo={`Por ${pc.campo.toLowerCase()}`} linhas={pc.linhas}
                colunas={[["Valor", (l) => l.valor], ["Total", (l) => fmt(l.total), "right"], ["Em andamento", (l) => fmt(l.abertos), "right"],
                  ...r.modelo.finais.map((fin) => [fin.split(" ")[0].toLowerCase().replace(/^./, (x) => x.toUpperCase()), (l) => fmt(l[fin] || 0), "right"])]} />
            </Grid>
          ))}
          <Grid item xs={12} md={6}>
            <Ranking titulo="Por atendente" subtitulo="Quem abriu e quem concluiu" linhas={r.atendentes} chaveBarra="abertos"
              colunas={[["Atendente", (l) => l.atendente], ["Abriu", (l) => fmt(l.abertos), "right"], ["Concluiu", (l) => fmt(l.concluidos), "right"]]} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Ranking titulo="Formulários" subtitulo="Enviados, respondidos e tempo médio de resposta" linhas={r.formularios} chaveBarra="enviados"
              colunas={[["Formulário", (l) => l.formulario], ["Enviados", (l) => fmt(l.enviados), "right"],
                ["Respondidos", (l) => `${fmt(l.respondidos)} (${pct(l.respondidos, l.enviados)})`, "right"],
                ["Resposta", (l) => (l.horas_resposta === null ? "-" : `${fmt(l.horas_resposta, 1)} h`), "right"]]} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

// ------------------------------------------------------------------ página: uma aba por área
const Relatorios = () => {
  const [aba, setAba] = useState("suporte");
  const [filas, setFilas] = useState([]);
  useEffect(() => {
    Promise.all([suporteApi.get("/api/formularios/filas"), suporteApi.get("/api/modelos-card")])
      .then(([q, m]) => setFilas(q.data.filter((x) => m.data.some((mc) => mc.fila_id === x.id))))
      .catch(() => {});
  }, []);
  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatórios</Title>
        <MainHeaderButtonsWrapper>
          <Tabs value={aba} onChange={(e, v) => setAba(v)} indicatorColor="primary" textColor="primary">
            <Tab value="suporte" label="Suporte" />
            {filas.map((q) => <Tab key={q.id} value={q.id} label={q.nome} />)}
          </Tabs>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      {aba === "suporte" ? <RelatorioGestao /> : <RelatorioFila key={aba} filaId={aba} />}
    </MainContainer>
  );
};

export default Relatorios;
